import { CheckCircle, SwitchHorizontal01 } from '@untitledui/icons';
import type { FC } from 'react';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { AhoyLogoMinimal } from '@/lib/foundations/logo/ahoy-logo-minimal';

import { setOAuthInstanceRoutingCookie } from '../../oauth-instance-cookie';
import { getHubspotInstallUrl } from '../api/install';
import { useHubspotStatus } from '../hooks/useHubspotStatus';
import { HubspotIcon } from '../HubspotIcon';

interface ConnectHubspotButtonProps {
  orgId: number | string;
  disabled?: boolean;
  returnTo?: string;
  intent?: string;
  heading?: string;
  description?: string;
  benefits?: readonly string[];
  connectorIcon?: FC<{ className?: string }>;
  connectorIconLabel?: string;
}

const DEFAULT_HEADING = 'Connect your HubSpot account';

const DEFAULT_DESCRIPTION =
  'Link HubSpot to keep contacts, companies, and deals in two-way sync with Ahoy – new records, property edits, and deletions flow automatically in both directions.';

const DEFAULT_BENEFITS = [
  'Import your HubSpot contacts and companies into Ahoy',
  'Keep names, emails, phone numbers, and custom fields in sync both ways',
  'Sync deals and pipelines with full two-way updates',
];

const BENEFIT_CLASS =
  'flex gap-2 text-body-small text-[var(--color-text-secondary)]';

const CONNECTOR_TILE_CLASS =
  'flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-xs';

const CARD_CLASS =
  'max-w-2xl rounded-xl border border-[var(--color-border-secondary)] p-6';

function ConnectSkeleton() {
  return (
    <div className={CARD_CLASS} aria-hidden='true'>
      <div className='flex items-center gap-3'>
        <Skeleton variant='rectangular' width={56} height={56} />
        <Skeleton variant='circular' width={20} height={20} />
        <Skeleton variant='rectangular' width={56} height={56} />
      </div>
      <div className='mt-5 space-y-2'>
        <Skeleton variant='text' width='45%' height={24} />
        <Skeleton variant='text' width='90%' />
        <Skeleton variant='text' width='70%' />
      </div>
      <div className='mt-4 space-y-2.5'>
        <Skeleton variant='text' width='60%' />
        <Skeleton variant='text' width='65%' />
        <Skeleton variant='text' width='55%' />
      </div>
      <Skeleton
        variant='rectangular'
        width={170}
        height={40}
        className='mt-6'
      />
    </div>
  );
}

export function ConnectHubspotButton({
  orgId,
  disabled,
  returnTo,
  intent,
  heading,
  description,
  benefits,
  connectorIcon,
  connectorIconLabel,
}: ConnectHubspotButtonProps) {
  const { isLoading: isStatusLoading } = useHubspotStatus(orgId);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const Heading = heading ?? DEFAULT_HEADING;
  const Description = description ?? DEFAULT_DESCRIPTION;
  const benefitItems = benefits ?? DEFAULT_BENEFITS;
  const ConnectorIcon = connectorIcon ?? SwitchHorizontal01;
  const connectorLabel = connectorIconLabel ?? 'two-way sync';

  const handleClick = useCallback(async () => {
    setIsConnecting(true);
    setError(null);
    try {
      const { url } = await getHubspotInstallUrl(orgId, { returnTo, intent });
      setOAuthInstanceRoutingCookie();
      window.location.href = url;
    } catch (e) {
      console.error(e);
      setError("We couldn't start the HubSpot connection. Try again.");
      setIsConnecting(false);
    }
  }, [orgId, returnTo, intent]);

  if (isStatusLoading) {
    return <ConnectSkeleton />;
  }

  return (
    <div className={CARD_CLASS}>
      <div className='flex items-center gap-3'>
        <span className={CONNECTOR_TILE_CLASS}>
          <HubspotIcon className='h-6 w-6 text-[var(--color-text-primary)]' />
        </span>
        <ConnectorIcon
          className='h-5 w-5 text-[var(--color-text-tertiary)]'
          aria-label={connectorLabel}
        />
        <span className={CONNECTOR_TILE_CLASS}>
          <AhoyLogoMinimal className='h-6 w-6' />
        </span>
      </div>

      <div className='mt-5 space-y-1'>
        <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
          {Heading}
        </h3>
        <p className='text-body-small text-[var(--color-text-secondary)]'>
          {Description}
        </p>
      </div>

      <ul className='mt-4 space-y-2'>
        {benefitItems.map(benefit => (
          <li key={benefit} className={BENEFIT_CLASS}>
            <CheckCircle
              className='mt-0.5 h-4 w-4 shrink-0 text-[var(--color-fg-success-primary)]'
              aria-hidden='true'
            />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      <div className='mt-6 flex flex-wrap items-center gap-x-4 gap-y-2'>
        <Button
          color='primary'
          size='md'
          onClick={handleClick}
          isDisabled={disabled || isConnecting}
          isLoading={isConnecting}
          showTextWhileLoading
        >
          {isConnecting ? 'Connecting…' : 'Connect HubSpot'}
        </Button>
        <span className='text-body-small text-[var(--color-text-tertiary)]'>
          Next, you'll choose how contacts, companies, and deals map.
        </span>
      </div>

      {error && <p className='mt-3 text-sm text-utility-error-700'>{error}</p>}
    </div>
  );
}
